import React, {lazy, Suspense, useEffect} from "react";
import {Route, Routes, useLocation} from "react-router";

import {SET_EXTERNAL_USER, SET_MUSEUM_USER_OR_ADMIN, useAppDispatch, useAppState} from "../AppContext";
import {FoldersProvider} from "../../folder/FoldersContext";
import {useAuthsState} from "../../auths/authsContext";
import {ProjectProvider, useProjectState} from "../../project/projectContext";
import {MyFoldersProvider} from "../../folder/MyFoldersContext";
import {DocumentProvider} from "../../documents/documentContext";
import {ProjectViewProvider} from "../../project/archeology/projectview/projectViewContext";
import * as Sentry from "@sentry/react";
import {RestrictedAccessInformation} from "../restrictedaccess/RestrictedAccessInformation";
import {DrawerProvider} from "../../damsdrawer/drawerFiltersContext";
import {DocumentRoute} from "../../document-route/DocumentRoute";

// ─── Lazy-loaded route components ────────────────────────────────────────────
//
// SearchLandingPage is the main landing page and carries the full search UI
// tree (SearchResults, ResultTableV2, damsfileupload3, DrawerProvider, etc.).
// Lazy-loading it splits it into a separate chunk so it does NOT inflate the
// initial bundle for users who deep-link to admin, folder, or project routes.
// Previously it was the only high-traffic route still imported eagerly while
// all other heavy routes already used React.lazy().
const SearchLandingPage = lazy(() => import('../../search/SearchLandingPage'));

// AllProjects only loads for users with the archaeology module.  Lazy-loading
// avoids shipping this code to everyone else.
const AllProjects = lazy(() => import('./AllProjects'));

// Project-view page — only visited when navigating to a specific project.
const ProjectView = lazy(() => import('../../project/ProjectView'));

const DamsFileUploadHome = lazy(() => import('../../damsfileupload3/DamsFileUploadHome'));
const FolderByIdRoute = lazy(() => import('./FolderByIdRoute'));
const AdminImportData = lazy(() => import('./AdminImportData'));
const AdministrateMetadataMapping = lazy(() => import('./AdministrateMetadataMapping'));
const AdminReindex = lazy(() => import('./AdminReIndex'));
const DAMSStats = lazy(() => import('../../admin/statistics/DAMSStats'));
const RegenerateMetadata = lazy(() => import('../../admin/RegenerateMetadata'));
const AdminProjectView = lazy(() => import('../../project/general/AdminProjectView'));
const UploadStepper = lazy(() => import('../../damsfileupload/UploadStepper'));
const AdminLocalAssets = lazy(() => import('../../admin-local-assets/AdminLocalAssets'));
const FoldersList = lazy(() => import('../../folder/list/FoldersList'));
const AddOrReIndexSingle = lazy(() => import('../../admin/add-or-reindex/AddOrReIndexSingle'));
const CreateProjectPage = lazy(() => import('../../project/CreateProjectPage'));
const UserDocuments = lazy(() => import('../../search/UserDocuments'));
const ObjectHealth = lazy(() => import('../../admin/object-health/ObjectHealth'));
const NbPublishLog = lazy(() => import('../../admin/nb-publish-log/NbPublishLog'));

// RestoreDocuments was previously imported eagerly despite being placed inside
// a <Suspense> block — an eager import in Suspense gives no code-splitting
// benefit.  Making it lazy actually defers its chunk until /restore is visited.
const RestoreDocuments = lazy(() => import('../../restore-documents/RestoreDocuments'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const SearchLandingPageRoute = () => {
    return (
        <Suspense fallback={<></>}>
            <SearchLandingPage/>
        </Suspense>
    );
};

export const MyFilesRoute = () => {
    return (
        <Suspense fallback={<></>}>
            <UserDocuments/>
        </Suspense>
    );
};

// Project-related routes below this line
export const NewProjectRoute = () => {
    return (
        <MyFoldersProvider>
            <ProjectProvider>
                <CreateProjectPage/>
            </ProjectProvider>
        </MyFoldersProvider>
    );
};

export const ProjectRoute = () => {
    return (
        <Suspense fallback={<></>}>
            <DrawerProvider>
                <ProjectViewProvider>
                    <ProjectView/>
                </ProjectViewProvider>
            </DrawerProvider>
        </Suspense>
    );
};

export const ProjectAdminRoute = () => {
    return (
        <FoldersProvider>
            <DocumentProvider>
                <ProjectViewProvider>
                    <AdminProjectView/>
                </ProjectViewProvider>
            </DocumentProvider>
        </FoldersProvider>
    );
};

/**
 * Component exposing the routes available to authenticated users or an external user.
 * External user: A user that has been given access via a shared folder,
 * without having been granted access to the DAMS application or a user without any association with a museum.
 * This user may be logged on via eKultur. If the user has not been given access via a shared folder, an
 * info dialog is shown, if granted access via a shared folder, the contents of the folder is shown.
 * @returns {JSX.Element}
 * @constructor
 */
export const RoutesAuthenticated = () => {
    const location = useLocation();
    // DE-1720 (ADR-0008): a document opened from the search shows /documents/<uniqueId> while its background page
    // (the search) stays rendered; without a background, /documents/<uniqueId> is the DE-1719 route. Pages inside
    // read the browser's location from BrowserLocationProvider (App).
    const routedLocation = location.state?.backgroundLocation ?? location;
    const appDispatch = useAppDispatch();
    const {externalUser} = useAppState();
    const {userData, userIsAuthenticated} = useAuthsState();
    const {hasArcheologyModule} = useProjectState();

    useEffect(() => {
        if (!userData) {
            return;
        }

        let accessMuseumBehalf = false;
        if (userData.appAccess?.museums) {
            const museums = userData.appAccess.museums;
            for (let i = 0, max = museums.length; i < max; i++) {
                if (museums[i].applications.find(a => a.id === window._env_.REACT_APP_ID)) {
                    accessMuseumBehalf = true;
                    break;
                }
            }
        }
        appDispatch({type: SET_MUSEUM_USER_OR_ADMIN, value: accessMuseumBehalf});

        if (userData.appAccess.museums.length === 0) {
            appDispatch({type: SET_EXTERNAL_USER});
        }
    }, [userData, appDispatch]);

    const getUploadV2Route = () => {
        return <Suspense fallback={<></>}>
            <DocumentProvider>
                <UploadStepper/>
            </DocumentProvider>
        </Suspense>;
    };

    const getUploadV3Route = () => {
        return <Suspense fallback={<></>}><DamsFileUploadHome/></Suspense>;
    };

    if (!userIsAuthenticated) {
        return <></>;
    } else if (userIsAuthenticated) {
        return (
            <>
                <RestrictedAccessInformation/>
                <SentryRoutes location={routedLocation}>
                    {/* Show the routes available to users that is granted access to the DAMS application */}
                    {!externalUser && (
                        <>
                            <Route path="/">
                                <Route index element={<SearchLandingPageRoute/>}/>
                                <Route path="search">
                                    <Route index element={<SearchLandingPageRoute/>}/>
                                    <Route path=":searchString" element={<SearchLandingPageRoute/>}/>
                                </Route>
                            </Route>

                            <Route exact path={"/upload"} element={getUploadV2Route()}/>
                            <Route exact path={"/upload/:folderId"} element={getUploadV2Route()}/>

                            <Route exact path={"/upload3"} element={getUploadV3Route()}/>
                            <Route exact path={"/upload3/:folderId"} element={getUploadV3Route()}/>

                            <Route exact path={"/my-files"} element={<MyFilesRoute/>}/>

                            {/* Folder routes */}
                            <Route exact path={"/my-folders/"} element={
                                                                    <Suspense fallback={<></>}>
                                                                       <DrawerProvider>
                                                                           <FoldersProvider>
                                                                               <FoldersList folderType={"myfolders"}/>
                                                                           </FoldersProvider>
                                                                       </DrawerProvider>
                                                                   </Suspense>}/>
                            <Route
                                exact
                                path={"/all-folders/"}
                                element={<Suspense fallback={<></>}>
                                    <DrawerProvider>
                                        <FoldersProvider>
                                            <FoldersList/>
                                        </FoldersProvider>
                                    </DrawerProvider>
                                </Suspense>}
                            />
                            <Route
                                exact
                                path={"/shared-folders/"}
                                element={<Suspense fallback={<></>}>
                                    <DrawerProvider>
                                        <FoldersProvider>
                                            <FoldersList folderType={"sharedfolders"}/>
                                        </FoldersProvider>
                                    </DrawerProvider>
                                </Suspense>}
                            />
                            {/* Admin routes */}
                            <Route exact path={"/restore"}
                                   element={<Suspense fallback={<></>}>
                                       <DocumentProvider>
                                           <RestoreDocuments/>
                                       </DocumentProvider>
                                   </Suspense>}/>
                            <Route exact path={"/reindex"}
                                   element={<Suspense fallback={<></>}><AdminReindex/></Suspense>}/>
                            <Route
                                exact
                                path={"/metadata-mapping"}
                                element={<Suspense fallback={<></>}><AdministrateMetadataMapping/></Suspense>}/>
                            <Route exact path={'/admin-local-assets'}
                                   element={<Suspense
                                       fallback={<></>}>
                                       <DocumentProvider><AdminLocalAssets/></DocumentProvider>
                                   </Suspense>}/>
                            <Route exact path={"/regeneratemetadata"}
                                   element={
                                       <Suspense fallback={<></>}>
                                           <DocumentProvider>
                                               <RegenerateMetadata/>
                                           </DocumentProvider>
                                       </Suspense>
                                   }/>
                            <Route exact path={"/object-health"}
                                   element={
                                       <Suspense fallback={<></>}>
                                           <DocumentProvider>
                                               <ObjectHealth/>
                                           </DocumentProvider>
                                       </Suspense>
                                   }/>
                            <Route exact path={"/add-to-index"}
                                   element={
                                       <Suspense fallback={<></>}>
                                           <DocumentProvider>
                                               <AddOrReIndexSingle/>
                                           </DocumentProvider>
                                       </Suspense>
                                   }/>
                            <Route exact path={"/import"}
                                   element={<Suspense fallback={<></>}><AdminImportData/></Suspense>}/>
                            <Route exact path={"/stats"} element={<Suspense fallback={<></>}><DAMSStats/></Suspense>}/>
                            <Route exact path={"/nb-publish-log"}
                                   element={<Suspense fallback={<></>}><NbPublishLog/></Suspense>}/>
                            {/* Project routes */}
                            {hasArcheologyModule && (<>
                                <Route
                                    path={"/new-project/:projectType"}
                                    element={<Suspense fallback={<></>}><NewProjectRoute/></Suspense>}
                                />
                                <Route
                                    exact
                                    path={"/project/:projectId"}
                                    element={<ProjectRoute/>}
                                />
                                <Route
                                    exact
                                    path={"/project/admin/:projectId"}
                                    element={<Suspense fallback={<></>}><ProjectAdminRoute/></Suspense>}
                                />
                                <Route
                                    exact
                                    path={"/all-projects"}
                                    element={<Suspense fallback={<></>}><AllProjects/></Suspense>}
                                />
                            </>)}
                        </>
                    )}
                    {/* A single document by its unique id (DE-1719); the backend decides what the user may read */}
                    <Route path={"/documents/:uniqueId"} element={<DocumentRoute/>}/>
                    {/* A single folder, must be accessible for all users, incl. external users */}
                    <Route
                        exact
                        path={"/folders/:folderId/"}
                        element={<Suspense fallback={<></>}><FolderByIdRoute/></Suspense>}
                    />
                </SentryRoutes>
            </>
        );
    }
};
